import { FC, ReactNode } from 'react';

/**
 * Renders a spec-summary subtitle.
 *
 * A plain string renders as before. An array renders as a bulleted list, one
 * item per line - which is the only reliable fix for RTL lists: a single
 * comma-joined Arabic string containing Latin colour names ("أسود Raven، Deep
 * Spaceمعدني، ...") gets reordered by the bidi algorithm, so the separators
 * land in the wrong visual positions. Giving each value its own block element
 * makes it its own bidi paragraph, so ordering is correct per line.
 */
export type SpecSubtitleValue = string | string[] | undefined;

interface SpecSubtitleProps {
  value: SpecSubtitleValue;
  fallback: SpecSubtitleValue;
  isRTL: boolean;
  /** Typography/width classes from the calling layout. */
  className?: string;
  /** Trailing content (e.g. a <sup>); string subtitles only. */
  children?: ReactNode;
}

const SpecSubtitle: FC<SpecSubtitleProps> = ({
  value,
  fallback,
  isRTL,
  className = '',
  children,
}) => {
  const raw = value ?? fallback;
  const dir = isRTL ? 'rtl' : 'ltr';

  if (Array.isArray(raw)) {
    // No bullets - one value per line. `dir` still matters per item: it keeps
    // mixed Arabic/Latin values ordered correctly within their own line.
    // Alignment matches every other row in the panel (the subtitle column sits
    // opposite the title, so it hugs the outer edge in both directions).
    return (
      <ul
        dir={dir}
        className={`${className} list-none space-y-0.5 ${
          isRTL ? 'text-left' : 'text-right'
        }`}
      >
        {raw.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
    );
  }

  return (
    <span
      dir={dir}
      className={`${className} ${isRTL ? 'text-left' : 'text-right'}`}
    >
      {raw || 'N/A'}
      {children}
    </span>
  );
};

export default SpecSubtitle;
